/**
 * A deploy replaces every content-hashed bundle. A tab that was already open
 * still holds the previous `main.[hash].js`, which asks for chunk filenames
 * that no longer exist — and the SPA rewrite answers those with index.html,
 * so the browser reports a chunk failure (and a stray `Unexpected token '<'`
 * from parsing HTML as JavaScript) instead of a clean 404.
 *
 * Reloading picks up the current index.html and its bundles. Cache headers
 * close the stale-entry-point window; this closes the open-tab one.
 */
const RELOAD_MARKER = 'chunk-reload-at';
// Long enough that a reload which did not help stops here and lets the real
// error surface; short enough that a later deploy in the same long-lived
// session can still self-heal.
const RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_FAILURE =
  /ChunkLoadError|Loading (?:chunk|CSS chunk) \S+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i;

const isChunkFailure = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === 'string') return CHUNK_FAILURE.test(value);
  const { name, message } = value as { name?: unknown; message?: unknown };
  return (
    (typeof name === 'string' && CHUNK_FAILURE.test(name)) ||
    (typeof message === 'string' && CHUNK_FAILURE.test(message))
  );
};

const reloadOnce = (): void => {
  let lastAttempt = 0;
  try {
    lastAttempt = Number(window.sessionStorage.getItem(RELOAD_MARKER)) || 0;
  } catch {
    return; // storage unavailable (private mode): never risk a reload loop
  }
  if (Date.now() - lastAttempt < RELOAD_COOLDOWN_MS) return;
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
  } catch {
    return;
  }
  window.location.reload();
};

export const isChunkLoadFailure = isChunkFailure;

export default function installChunkReloadRecovery(): void {
  window.addEventListener('error', (event) => {
    if (isChunkFailure(event.error ?? event.message)) reloadOnce();
  });
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkFailure(event.reason)) reloadOnce();
  });
}
